import styled, { css } from 'styled-components';
import mediaQueries from '@Utils/shared/constants/mediaQueries';
import { ButtonColors, ButtonSizes } from './Button';

interface ButtonProps {
  color?: keyof typeof ButtonColors;
  size?: keyof typeof ButtonSizes;
  fluid?: boolean;
  isIcon?: boolean;
  isNoBoxShadow?: boolean;
}

const ButtonStyles = (props: ButtonProps) => css`
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: normal;
  color: #ffffff !important;
  text-align: center;
  border: none;
  border-radius: 4px;
  transition: 0.2s;
  cursor: pointer;
  box-shadow: 0 15px 20px -10px rgba(0, 0, 0, 0.3);

  &:disabled {
    cursor: not-allowed;
  }

  ${props.size === ButtonSizes.extraSmall &&
  css`
    min-height: 30px;
    font-size: 15px;
    line-height: 30px;
    font-weight: 400;
    padding: 0 20px;
    min-width: 130px;
  `}

  ${props.size === ButtonSizes.small &&
  css`
    min-width: 110px;
    min-height: 35px;
    padding: 0 25px;
    font-size: 16px;
    line-height: 35px;
  `};

  ${props.size === ButtonSizes.medium &&
  css`
    min-width: 120px;
    min-height: 40px;
    padding: 0 25px;
    font-size: 18px;
    line-height: 40px;

    @media ${mediaQueries.mobileOrTablet} {
      min-width: 100px;
      min-height: 35px;
      line-height: 35px;
      padding: 0 10px;
      font-size: 16px;
    }
  `}

  ${props.size === ButtonSizes.large &&
  css`
    min-width: 140px;
    min-height: 50px;
    line-height: 1.2;
    padding: 0 30px;
    font-size: 20px;
  `}

  ${props.color === ButtonColors.default &&
  css`
    background-color: var(--bg-white);
    color: #1f7cdf !important;
    border-radius: 6px;
    box-shadow: 0 8px 10px -10px rgba(0, 0, 0, 0.4);

    &.log-in-button {
      min-height: 35px;
      min-width: 0;
      padding: 0 17px;
      font-size: 15px;
      color: #1f7cdf;

      &:hover,
      &:active,
      &:disabled {
        color: #1f7cdf;
      }

      @media ${mediaQueries.mobileOrTablet} {
        padding: 0 22px;
      }
    }

    &:hover,
    &:active,
    &:disabled {
      color: #1f7cdf;
    }

    &:hover {
      background-color: var(--button-default-hover);
    }

    &:active {
      background-color: var(--button-default-press);
    }

    &:disabled {
      background-color: var(--button-default-disablet);
    }
  `}

  ${props.color === ButtonColors.dark &&
  css`
    background-color: #635c7c;
    background: linear-gradient(to bottom, #635c7c, #514b67 97%);

    &:hover {
      background-color: #6f6985;
      background: linear-gradient(to bottom, #6f6985, #635c7c 97%);
    }

    &:active {
      background-color: #635c7c;
      background: linear-gradient(to bottom, #635c7c, #514b67 97%);
    }

    &:disabled {
      background: #a9a6b5;
    }
  `}

  ${props.color === ButtonColors.primary &&
  css`
    background-color: #fa5400;
    background: linear-gradient(to bottom, #fa5400 -7%, #fa3e00 105%);

    &:hover {
      background-color: #ff681b;
      background: linear-gradient(to bottom, #ff681b -7%, #ff5118 104%);
    }

    &:active {
      background-color: #f04600;
      background: linear-gradient(to bottom, #f04600 -7%, #dd3700 101%);
    }

    &:disabled {
      background: #ffa582;
    }
  `}

  ${props.color === ButtonColors.secondary &&
  css`
    background-color: #5b8fe5;
    background: linear-gradient(to bottom, #5b8fe5, #4779cb 99%);

    &:hover {
      background-color: #619cff;
      background: linear-gradient(to bottom, #619cff, #558ce6);
    }

    &:active {
      background-color: #578adf;
      background: linear-gradient(to bottom, #578adf, #406cb5 99%);
    }

    &:disabled {
      background: #7daeff;
    }
  `}

  ${props.color === ButtonColors.defaultNormal &&
  css`
    background-color: #f0f0f0;
    color: #555557 !important;

    &:hover {
      background-color: #f4f4f4;
      color: #555557;
    }

    &:active {
      background-color: #f4f4f4;
      color: #555557;
    }

    &:disabled {
      background: #d2d2d2;
      color: #555557;
    }
  `}

  ${props.color === ButtonColors.purple &&
  css`
    background-color: #7c4795;
    background: linear-gradient(to bottom, #7c4795, #5d2a7e 100%);

    &:hover {
      background-color: #8f61a4;
      background: linear-gradient(to bottom, #8f61a4, #744890 100%);
    }

    &:active {
      background-color: #6a3c7f;
      background: linear-gradient(to bottom, #6a3c7f, #4f246c 100%);
    }

    &:disabled {
      background-color: #bfa5cb;
      background: linear-gradient(to bottom, #bfa5cb, #ae94be 100%);
    }
  `}

  ${props.fluid &&
  css`
    width: 100%;
    padding: 0;
  `}

  &.game-header-details-button {
    margin-top: 5px;
    margin-bottom: 10px;
    margin-left: 25px;
  }

  &.game-header-fast-download-button {
    line-height: 20px;
  }

  ${props.isIcon &&
  css`
    padding: 0 25px 0 20px;
  `}

  ${props.isNoBoxShadow &&
  css`
    box-shadow: none;
  `}
`;

export const Button = styled.button<ButtonProps>`
  ${(props) => ButtonStyles(props)}

  ${(props) =>
    props.isIcon &&
    css`
      margin-left: 10px;
    `}
`;

export const ButtonLink = styled('a')<ButtonProps>`
  ${(props) => ButtonStyles(props)}
`;

export const IconContent = styled.i`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  width: 20px;
  height: auto;

  & svg {
    width: 100%;
    height: auto;
  }
`;
